import type { AiIdentification, AiIdentificationSummary } from "@/services/aiIdentification/aiIdentificationTypes";
import { CategoryId } from "@/services/collection/categories/categoriesSchema";
import type { CollectionItem } from "@/services/collection/item";
import { createCollectionItemFromCollectible } from "@/services/collection/item/collectionItemFromCollectible";
import { setImagesFromAiIdentification } from "@/services/collection/item/gallery";
import * as PostHog from "@/services/analytics/postHog";
import { factoryImage, type Image } from "@/services/images";
import type { PriceNumeric } from "@/services/prices/priceTypes";
import { isNumericPrice } from "@/services/prices/priceValidators";

type Photos = { front: File; back?: File };

export const createCollectionItemFromIdentification = async (identification: AiIdentification, photos?: Photos): Promise<CollectionItem> => {
  const collectible = identification.collectible;
  if (collectible === null || collectible === undefined) {
    throw new Error("It should not be possible to create collection item from old ai identification without collectible.");
  }

  const item = await createCollectionItemFromCollectible({
    collectibleId: collectible.id,
    categoryId: collectible.categoryId,
    photos,
    aiIdentificationSourceId: identification.id,
    publicNote: identification.description ?? "",
  });

  if (!photos) {
    await setImagesFromAiIdentification(item, identification);
  }

  PostHog.trackEvent(PostHog.EventName.AiIdentification_ItemCreated);
  return item;
};

export const SupportedCategoryIds = [CategoryId.SportsCards, CategoryId.Pokemon];

export const getImages = (item: AiIdentificationSummary | AiIdentification): Image[] => item.images.map(({ imageUrl }) => factoryImage(0 /* fake ID */, imageUrl, item.name));

export const hasPrice = (item: AiIdentificationSummary | AiIdentification): item is (AiIdentification | AiIdentification) & { priceFrom: PriceNumeric; priceTo: PriceNumeric } => isNumericPrice(item.priceFrom) && isNumericPrice(item.priceTo);
